/**
 * A single-choice dropdown over a tenant pick list.
 *
 * Categories, services and cause codes all arrive as `TicketLookupItem[]` whose
 * labels are `TranslatableText`, so they share one control rather than three
 * near-identical `Select` trees. An empty list renders a disabled trigger with
 * an explanatory placeholder — the tenant profile may not have resolved yet
 * (503 `SERVICE_STARTING`), and a silently empty dropdown looks broken.
 */

import { Select } from "@norican/react";

import type { TicketLookupItem } from "../api/contract";
import { displayText } from "../lib/translatable";

export interface LookupSelectProps {
  items: TicketLookupItem[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder: string;
  /** Label for the option that clears the choice. Omit to make it mandatory. */
  clearLabel?: string;
  name?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  "aria-label"?: string;
}

/** Sentinel for the "no choice" option — Base UI selects need a real value. */
const NONE = "__none__";

export function LookupSelect({
  items,
  value,
  onValueChange,
  placeholder,
  clearLabel,
  name,
  disabled = false,
  size = "md",
  "aria-label": ariaLabel,
}: LookupSelectProps) {
  const options = [
    ...(clearLabel === undefined ? [] : [{ value: NONE, label: clearLabel }]),
    ...items.map((item) => ({ value: item.id, label: displayText(item.label) })),
  ];

  // Base UI shows the trigger placeholder for a null value, so "nothing chosen"
  // is `null` unless the caller offers an explicit clear option.
  const selectValue = value ?? (clearLabel === undefined ? null : NONE);

  return (
    <Select.Root
      name={name}
      value={selectValue}
      items={options}
      disabled={disabled || items.length === 0}
      onValueChange={(next) => {
        const selected = String(next);
        onValueChange(selected === NONE ? null : selected);
      }}
    >
      <Select.Trigger size={size} placeholder={placeholder} aria-label={ariaLabel} />
      <Select.Portal>
        <Select.Positioner sideOffset={4}>
          <Select.Popup width="anchor">
            <Select.List>
              {options.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}
